import {useQuery} from 'react-query';
import DeviceService from '../../../../apis/DeviceService';
import OwnerAssociationService from '../../../../apis/OwnerAssociationService';
import OwnersService from '../../../../apis/OwnersService';
import DataStreamService from '../../data-streams/DataStreamService';

const ownedBy = (rows, ownerId) => (rows || []).filter((row) => row.owner_id === ownerId);

// device-view carries owner_id, but it is the owner name the grid shows; match on either so
// a view that ever drops the id still lists the right devices.
const devicesOf = (rows, owner) => (rows || []).filter((device) =>
  device.owner_id === owner?.id
  || (device.owner_name && owner?.name && device.owner_name === owner.name));

// An association names only the child's id, so the owner list supplies the rest. A child
// the list does not carry still gets a row: its absence is worth seeing, not hiding.
const locationsOf = (associations, owners) => (associations || []).map((association) => {
  const child = (owners || []).find((owner) => owner.id === association.child_owner_id);
  return {
    id: association.child_owner_id,
    name: child?.name || `Owner ${association.child_owner_id}`,
    timezone: child?.timezone || '',
    disabled: Boolean(child?.disabled),
  };
});

// Everything the owner view shows about one owner beyond its own row. Each list is fetched
// under the key the matching page already uses, so opening an owner warms those pages
// rather than fetching a second copy, and none of it runs until the dialog is open.
const useOwnerRelations = (owner, enabled) => {
  const ownerId = owner?.id;
  const on = Boolean(enabled && ownerId);

  const dataStreams = useQuery('getDataStreams', DataStreamService.getAll, {
    staleTime: Infinity,
    enabled: on,
  });

  const devices = useQuery('getDevices', DeviceService.getDevices, {
    staleTime: Infinity,
    enabled: on,
  });

  // Same key the data stream editor uses for a parent's children.
  const childOwners = useQuery(
    ['childOwners', ownerId],
    () => OwnerAssociationService.getOneByParentId(ownerId),
    {enabled: on && Boolean(owner?.multi_site)}
  );

  const owners = useQuery('getOwners', OwnersService.getAllSortedByName, {
    staleTime: Infinity,
    enabled: on && Boolean(owner?.multi_site),
  });

  return {
    dataStreams: ownedBy(dataStreams.data, ownerId),
    dataStreamsLoading: dataStreams.isLoading,
    devices: devicesOf(devices.data, owner),
    devicesLoading: devices.isLoading,
    locations: locationsOf(childOwners.data, owners.data),
    locationsLoading: childOwners.isLoading || owners.isLoading,
  };
};

export {ownedBy, devicesOf, locationsOf};
export default {useOwnerRelations, ownedBy, devicesOf, locationsOf};
